<script lang="js">
import NotificationService from "@/utils/NotificationService";
import URLHelper from "@/utils/URLHelper";

export default {
  name: "Notification Dropdown",
  props: {
    user: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      logger: null,
      notifications: [],
      isLoading: true,
      refreshInterval: null,
    };
  },
  created() {
    // Create a logger instance for this component
    this.logger = this.$createLogger('NotificationDropdown');
  },
  async mounted() {
    if (this.user) {
      // Fetch notifications
      this.fetchNotifications(this.user);
      this.startAutoRefresh(this.user);
    }

  },
  computed: {
    validNotifications() {
      return this.notifications.filter(notification => notification && notification.type);
    }
  },
  methods: {
    getCorrectUrl(path) {
      // Use the enhanced URLHelper method that handles all path translations
      return URLHelper.getCorrectUrl(path);
    },
    async fetchNotifications(userData) {
      this.isLoading = true;
      try {
        // Use the method that combines system and staff notifications
        const allNotifications = await NotificationService.getAllNotificationsWithStaff(userData);
        this.notifications = allNotifications;
      } catch (error) {
        this.logger.error("Error fetching notifications:", error);
      } finally {
        this.isLoading = false;
      }
    },
    truncateMessage(message) {
      if (!message) return '';
      return message.length > 100 ? message.slice(0, 100) + '...' : message;
    },
    timeAgo(dateString) {
      const now = new Date();
      const date = new Date(dateString);

      const diffInMs = now - date;
      const diffInSeconds = Math.floor(diffInMs / 1000);
      const diffInMinutes = Math.floor(diffInSeconds / 60);
      const diffInHours = Math.floor(diffInMinutes / 60);
      const diffInDays = Math.floor(diffInHours / 24);

      if (diffInDays >= 7) {
        const weeks = Math.floor(diffInDays / 7);
        return `${weeks} week${weeks > 1 ? "s" : ""} ago`;
      } else if (diffInDays > 0) {
        return `${diffInDays} day${diffInDays > 1 ? "s" : ""} ago`;
      } else if (diffInHours > 0) {
        return `${diffInHours} hour${diffInHours > 1 ? "s" : ""} ago`;
      } else if (diffInMinutes > 0) {
        return `${diffInMinutes} minute${diffInMinutes > 1 ? "s" : ""} ago`;
      } else {
        return null;
      }
    },
    startAutoRefresh(userData) {
      this.refreshInterval = setInterval(() => {
        this.fetchNotifications(userData);
      }, 30000);
    },

  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  },
  components: {}
};
</script>

<template>
  <div class="dropdown me-4">
    <a
      href="#"
      class="position-relative d-inline-block dropdown-toggle"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
    >
      <i class="bi bi-bell-fill fs-5 text-white"></i>
      <span
        v-if="validNotifications.length > 0"
        class="position-absolute notification-badge translate-middle p-1 bg-danger rounded-circle"
        style="width: 10px; height: 10px"
      >
        <span class="visually-hidden">New alerts</span>
      </span>
    </a>

    <ul
      class="dropdown-menu dropdown-menu-end border-0 shadow pb-0"
      style="width: 350px !important; height: 500px"
    >
      <li class="small mb-2 fw-semibold dropdown-header" style="z-index: 10">
        Notifications
      </li>
      <li>
        <hr class="dropdown-divider" />
      </li>
      <!-- Scrollable List -->
      <div class="overflow-scroll h-100" style="max-height: 450px">
        <!-- Show placeholders when loading -->
        <template v-if="isLoading">
          <li v-for="n in 5" :key="n" class="border-bottom py-2 px-3">
            <div class="placeholder-glow">
              <span class="placeholder col-7"></span>
            </div>
            <div class="placeholder-glow">
              <span class="placeholder col-11"></span>
            </div>
            <div class="placeholder-glow">
              <span class="placeholder col-3"></span>
            </div>
          </li>
        </template>

        <!-- Show notifications -->
        <template v-else-if="validNotifications.length > 0">
          <li
            class="border-bottom py-2 dropdown-item-text"
            v-for="(notification, index) in validNotifications"
            :key="index"
          >
            <h6
              class="small m-0 text-truncate"
              style="max-width: 100%; white-space: nowrap; overflow: hidden"
            >
              {{ notification.title || "N/A" }}
            </h6>
            <p class="small mb-1 text-wrap text-secondary">
              {{ notification.message || "No message available" }}
              <a
                v-if="notification.link"
                :href="getCorrectUrl(notification.link)"
              >
                Click here
              </a>
            </p>
            <span class="small text-muted" v-if="notification.date">
              <i class="bi bi-clock me-1"></i> {{ timeAgo(notification.date) }}
            </span>
          </li>
        </template>

        <!-- Show empty state -->
        <li v-else class="text-center text-muted py-4 small">
          <i class="bi bi-inbox-fill fs-4 d-block mb-2 text-secondary"></i>
          No Notifications
        </li>
      </div>
    </ul>
  </div>
</template>

<style scoped>
.dropdown-toggle::after {
  display: none;
}

.notification-badge {
  left: 80% !important;
  top: 25% !important;
}
</style>
